import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { RejectCandidateConfirmationPopup } from '~/components/PermanentJobsV2/JobOverview';
import { DeclineCandidateConfirmationPopup } from '~/components/PermanentJobsV2/JobOverview/components';
import { EnablePermanentJobM2M } from '~/constants/ff.posthog';
import { useConfirmJobMutation, useDeclineCandidateMutation } from '~/domain/Jobs';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import {
  fireConfirmCandidateConfirmClicked,
  fireConfirmCandidateDeclineClicked,
  fireConfirmCandidateDeclineConfirmed,
} from '~/posthogs/events';
import { NO_CARD_ERROR, SHOW_MESSAGE } from '../../../actions';
import { Button } from '@tempmee/react';
import { isPartialShift } from '../../../commonComponents/CommonFunctions';
import CounterShiftPopup from '../../CounterShiftPopup';
import DeclineCandidatePopup from '../../DeclineCandidatePopup';

const getAdjacentCounterOffer = (allCandidates = [], counterOfferId) => {
  const declinedIndex = allCandidates.findIndex(
    (counterOffer) => counterOffer?.id === counterOfferId,
  );

  if (declinedIndex === -1) {
    return null;
  }

  return allCandidates[declinedIndex + 1] ?? allCandidates[declinedIndex - 1] ?? null;
};

const getMutationErrorMessage = (error) => {
  const responseData = error?.response?.data;

  if (typeof responseData === 'string') {
    return responseData;
  }

  return (
    responseData?.message ||
    responseData?.error ||
    responseData?.title ||
    error?.message ||
    'An unexpected error occurred.'
  );
};

export const ConfirmOrDecline = ({
  candidateId,
  job,
  currentCounter: providedCurrentCounter,
  allCandidates,
  notesForTemp,
  allowNavigation,
  onDeclineConfirmed,
  onConfirmOutcome,
  onConfirmPendingChange,
  onDeclinePendingChange,
  onDeclineSuccess,
  onPaymentMissing,
  small = false,
}) => {
  const dispatch = useDispatch();
  const isEnablePermanentJobM2M = useFeatureFlag(EnablePermanentJobM2M);
  const confirmJobMutation = useConfirmJobMutation({
    onOutcome: ({ outcome, response }, variables) => {
      if (outcome.type === 'show_another_job_popup') {
        onConfirmOutcome?.({ outcome, response }, variables);
        return;
      }

      if (outcome.type === 'show_eligible_jobs_popup') {
        onConfirmOutcome?.({ outcome, response }, variables);
        return;
      }

      if (outcome.type === 'onsite_confirmed') {
        const m2mOnsiteInterviewMessage =
          `Good news, ${variables.candidate?.full_name} has been confirmed ` +
          `for your onsite interview on ${outcome.localDate}.`;

        dispatch({
          type: SHOW_MESSAGE,
          payload: isEnablePermanentJobM2M
            ? {
                title: 'Interview Confirmed!',
                message: m2mOnsiteInterviewMessage,
                route: `/permanent-jobs/job-overview/${outcome.postingId}`,
                isSuccess: true,
              }
            : {
                title: 'Good News!',
                message: 'Your onsite interview has successfully been filled.',
                route: `/permanent-jobs/job-overview/${outcome.postingId}`,
                isSuccess: true,
              },
        });
        return;
      }

      if (outcome.type === 'confirmed_no_followup') {
        dispatch({
          type: SHOW_MESSAGE,
          payload: {
            title: 'Good News!',
            message: 'Your shift has successfully been filled.',
            route: outcome.route,
            isSuccess: true,
          },
        });
      }
    },
  });
  const declineCandidateMutation = useDeclineCandidateMutation();

  const [declineCandidatePopup, setDeclineCandidatePopup] = useState(false);
  const [counterShiftPopup, setCounterShiftPopup] = useState(false);
  const currentCounter =
    providedCurrentCounter ??
    allCandidates?.find((counter) => counter?.candidate?.id === candidateId);
  const isCounterHours = isPartialShift(currentCounter);

  const isOnsiteInterview = job?.isOnsiteInterview;

  const handleMutationError = (error) => {
    if (error?.response?.status === NO_CARD_ERROR) {
      onPaymentMissing?.();
      return;
    }

    dispatch({
      type: SHOW_MESSAGE,
      payload: {
        title: 'Sorry!',
        message: getMutationErrorMessage(error),
        isError: true,
        buttonText: 'Close',
      },
    });
  };

  const declineCandidatePressed = () => {
    fireConfirmCandidateDeclineClicked({ shiftId: job.id });
    setDeclineCandidatePopup(true);
  };

  const handleAcceptClicked = () => {
    setCounterShiftPopup(false);
    confirmOffer();
  };

  const handleCancelClicked = () => {
    setCounterShiftPopup(false);
  };

  const handleConfirmDecline = (reason = '') => {
    fireConfirmCandidateDeclineConfirmed({ shiftId: job.id });
    onDeclineConfirmed?.(allCandidates?.length > 1);
    const declinePayload = {
      jobId: job.id,
      counterOfferId: currentCounter?.id,
      cancellation_reason: reason,
      candidateId: currentCounter?.candidate?.id,
    };

    const nextCandidateId = !isOnsiteInterview
      ? getAdjacentCounterOffer(allCandidates, currentCounter?.id)?.candidate?.id
      : null;

    if (nextCandidateId) {
      declinePayload.nextCandidateId = nextCandidateId;
    }

    void (async () => {
      onDeclinePendingChange?.(true);
      try {
        const response = await declineCandidateMutation.mutateAsync(declinePayload);
        if (response?.isOnsiteInterview) {
          onDeclineSuccess?.();
        }
      } catch (error) {
        handleMutationError(error);
      } finally {
        onDeclinePendingChange?.(false);
      }
    })();
    setDeclineCandidatePopup(false);
  };

  const confirmOffer = () => {
    fireConfirmCandidateConfirmClicked({ shiftId: job.id });
    allowNavigation();

    void (async () => {
      onConfirmPendingChange?.(true);
      try {
        await confirmJobMutation.mutateAsync({
          jobId: job.id,
          data: { notes: notesForTemp },
          counterOfferId: currentCounter?.id,
          candidate: currentCounter?.candidate,
          officeId: job?.offer_owner?.id,
        });
      } catch (error) {
        handleMutationError(error);
      } finally {
        onConfirmPendingChange?.(false);
      }
    })();
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'center',
      }}
    >
      <Button
        variant="secondary"
        size={small ? 'xs' : 'md'}
        style={{ marginRight: !small ? 20 : 12 }}
        onClick={declineCandidatePressed}
      >
        Decline
      </Button>
      <Button
        size={small ? 'xs' : 'md'}
        onClick={() => {
          if (isCounterHours) {
            setCounterShiftPopup(true);
          } else {
            confirmOffer();
          }
        }}
      >
        Confirm
      </Button>

      {declineCandidatePopup && (
        <>
          {isOnsiteInterview ? (
            <>
              {isEnablePermanentJobM2M ? (
                <DeclineCandidateConfirmationPopup
                  isLoading={declineCandidateMutation.isPending}
                  jobLocalDate={job.local_date}
                  professionalName={`${currentCounter?.candidate?.first_name} ${currentCounter?.candidate?.last_name}`}
                  onClose={() => setDeclineCandidatePopup(false)}
                  onConfirm={() => handleConfirmDecline('')}
                />
              ) : (
                <RejectCandidateConfirmationPopup
                  isLoading={declineCandidateMutation.isPending}
                  professionalName={`${currentCounter?.candidate?.first_name} ${currentCounter?.candidate?.last_name}`}
                  useDeclineTerms
                  onClose={() => setDeclineCandidatePopup(false)}
                  onConfirm={() => handleConfirmDecline('')}
                />
              )}
            </>
          ) : (
            <DeclineCandidatePopup
              declineBtnPressed={handleConfirmDecline}
              goBackPressed={() => setDeclineCandidatePopup(false)}
            />
          )}
        </>
      )}

      {counterShiftPopup ? (
        <CounterShiftPopup
          job={job}
          currentCounter={currentCounter}
          acceptBtnPressed={handleAcceptClicked}
          cancelBtnPressed={handleCancelClicked}
        />
      ) : null}
    </div>
  );
};
